import { useCallback, useEffect, useState } from 'react';
import { GRADES, getGrade, type Grade } from '@numslearn/shared';
import { useSettings } from '../settings.js';
import {
  api, ApiError,
  type AuthPolicy, type AuthSession, type ClassRow, type LeaderRow, type RosterRow,
  type Teacher as TeacherAccount,
} from '../net/api.js';
import { navigate } from '../router.js';
import { MediaManager } from '../admin/MediaManager.js';
import { useMedia } from '../media.js';
import type { StringKey } from '../i18n/index.js';

/** Sign in, then run classes: roster, leaderboard, and starting a session. */
export function Teacher() {
  const [me, setMe] = useState<TeacherAccount | null | undefined>(undefined);
  const [policy, setPolicy] = useState<AuthPolicy | null>(null);
  const { refresh: refreshMedia } = useMedia();

  // Signing in or out changes whose pictures the boards draw from.
  useEffect(() => { if (me !== undefined) void refreshMedia(); }, [me, refreshMedia]);

  useEffect(() => {
    api.get<AuthSession>('/auth/session')
      .then((s) => { setMe(s.teacher); setPolicy(s.policy); })
      .catch(() => setMe(null));
  }, []);

  if (me === undefined) return <div className="tpanel"><p className="a-empty">…</p></div>;
  if (me === null) return <SignIn policy={policy} onSignedIn={setMe} />;
  return <Classes me={me} onSignOut={() => { void api.post('/auth/logout').then(() => setMe(null)); }} />;
}

function SignIn({ policy, onSignedIn }: { policy: AuthPolicy | null; onSignedIn: (t: TeacherAccount) => void }) {
  const { t } = useSettings();
  const [mode, setMode] = useState<'in' | 'up'>(policy?.first ? 'up' : 'in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The first account on a fresh server is a sign-up, not a sign-in.
  useEffect(() => { if (policy?.first) setMode('up'); }, [policy]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const path = mode === 'in' ? '/auth/login' : '/auth/register';
      const body = mode === 'in' ? { email, password } : { email, password, name, code };
      onSignedIn(await api.post<TeacherAccount>(path, body));
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="tauth" onSubmit={submit}>
      <h1>{mode === 'in' ? t('teacher.signIn') : t('teacher.signUp')}</h1>

      {mode === 'up' && (
        <label className="field">
          <span>{t('teacher.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </label>
      )}
      <label className="field">
        <span>{t('teacher.email')}</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      </label>
      <label className="field">
        <span>{t('teacher.password')}</span>
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
        />
      </label>
      {mode === 'up' && policy?.needsCode && !policy.first && (
        <label className="field">
          <span>{t('teacher.regCode')}</span>
          <input value={code} onChange={(e) => setCode(e.target.value)} />
        </label>
      )}

      {error && <p className="join-err" role="alert">{t(`teacher.err.${error}` as StringKey) || t('teacher.err.generic')}</p>}

      <div className="join-acts">
        <button className="gbtn pri" type="submit" disabled={busy}>
          {mode === 'in' ? t('teacher.submitIn') : t('teacher.submitUp')}
        </button>
        <button className="gbtn" type="button" onClick={() => navigate('/')}>{t('nav.home')}</button>
      </div>

      {(policy?.open || policy?.first) && (
        <button className="linkish" type="button" onClick={() => setMode(mode === 'in' ? 'up' : 'in')}>
          {mode === 'in' ? t('teacher.toSignUp') : t('teacher.toSignIn')}
        </button>
      )}
    </form>
  );
}

function Classes({ me, onSignOut }: { me: TeacherAccount; onSignOut: () => void }) {
  const { t, num } = useSettings();
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [open, setOpen] = useState<ClassRow | null>(null);
  const [name, setName] = useState('');
  const [grade, setGrade] = useState<Grade>(2);

  const reload = useCallback(() => {
    api.get<ClassRow[]>('/classes').then(setClasses).catch(() => undefined);
  }, []);
  useEffect(reload, [reload]);

  if (open) return <ClassDetail row={open} onBack={() => { setOpen(null); reload(); }} />;

  return (
    <div className="tpanel">
      <header className="tpanel-hd">
        <div>
          <h1>{t('teacher.classes')}</h1>
          <p>{me.name} · {me.email}</p>
        </div>
        <div className="tpanel-acts">
          <button className="gbtn" onClick={() => navigate('/')}>{t('nav.home')}</button>
          <button className="gbtn" onClick={onSignOut}>{t('teacher.signOut')}</button>
        </div>
      </header>

      <form
        className="tcard"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim().length < 2) return;
          void api.post<ClassRow>('/classes', { name: name.trim(), grade }).then(() => { setName(''); reload(); });
        }}
      >
        <h2>{t('teacher.newClass')}</h2>
        <div className="trow">
          <label className="field">
            <span>{t('teacher.className')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="4B" />
          </label>
          <label className="field">
            <span>{t('admin.grade')}</span>
            <select className="a-select" value={grade} onChange={(e) => setGrade(Number(e.target.value) as Grade)}>
              {GRADES.map((g) => (
                <option key={g.grade} value={g.grade}>{t('msg.grade', { g: g.grade })}</option>
              ))}
            </select>
          </label>
          <button className="gbtn pri" type="submit">{t('teacher.create')}</button>
        </div>
      </form>

      {/* Pictures live here, on the account page, not only behind the gear in
          a running game: a teacher gets their photos ready while planning the
          lesson, and looking for an upload button inside a live board in front
          of thirty children is not the moment. */}
      <section className="tcard">
        <h2>{t('media.title')}</h2>
        <p className="a-hint">{t('media.subtitle')}</p>
        <MediaManager />
      </section>

      {classes.length === 0 ? (
        <p className="a-empty">{t('teacher.noClasses')}</p>
      ) : (
        <ul className="class-list">
          {classes.map((c) => (
            <li key={c.id}>
              <button className="class-open" onClick={() => setOpen(c)}>
                <strong>{c.name}</strong>
                <small>{t('msg.grade', { g: c.grade })} · {t('teacher.students', { n: c.students })}</small>
              </button>
              <span className="class-code">{c.code}</span>
              {c.live && <span className="class-live">{t('teacher.live')}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClassDetail({ row, onBack }: { row: ClassRow; onBack: () => void }) {
  const { t, num } = useSettings();
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [board, setBoard] = useState<LeaderRow[]>([]);
  const [boardTeacher, setBoardTeacher] = useState('');
  const [mode, setMode] = useState<'lockstep' | 'race'>('lockstep');
  const [grade, setGrade] = useState<Grade>(row.grade as Grade);
  const [levels, setLevels] = useState<number[]>([1]);

  const reload = useCallback(() => {
    api.get<RosterRow[]>(`/classes/${row.id}/roster`).then(setRoster).catch(() => undefined);
    api.get<{ teacherName: string; rows: LeaderRow[] }>(`/classes/${row.id}/leaderboard`)
      .then((r) => { setBoard(r.rows); setBoardTeacher(r.teacherName); })
      .catch(() => undefined);
  }, [row.id]);
  useEffect(reload, [reload]);

  const def = getGrade(grade);

  return (
    <div className="tpanel">
      <header className="tpanel-hd">
        <div>
          <h1>{row.name}</h1>
          <p>{t('msg.grade', { g: row.grade })} · {t('home.code')} <b>{row.code}</b></p>
        </div>
        <div className="tpanel-acts">
          <button className="gbtn" onClick={onBack}>{t('teacher.classes')}</button>
          <button
            className="gbtn danger"
            onClick={() => {
              if (!window.confirm(t('teacher.deleteAsk'))) return;
              void api.del(`/classes/${row.id}`).then(onBack);
            }}
          >{t('teacher.deleteClass')}</button>
        </div>
      </header>

      <section className="tcard">
        <h2>{t('teacher.startSession')}</h2>
        <div className="trow">
          <label className="field">
            <span>{t('teacher.mode')}</span>
            <div className="a-seg">
              <button type="button" aria-pressed={mode === 'lockstep'} onClick={() => setMode('lockstep')}>
                {t('teacher.lockstep')}
              </button>
              <button type="button" aria-pressed={mode === 'race'} onClick={() => setMode('race')}>
                {t('teacher.race')}
              </button>
            </div>
            <small>{t(mode === 'race' ? 'teacher.raceHint' : 'teacher.lockstepHint')}</small>
          </label>

          <label className="field">
            <span>{t('admin.grade')}</span>
            <select
              className="a-select" value={grade}
              onChange={(e) => { setGrade(Number(e.target.value) as Grade); setLevels([1]); }}
            >
              {GRADES.map((g) => <option key={g.grade} value={g.grade}>{t('msg.grade', { g: g.grade })}</option>)}
            </select>
          </label>
        </div>

        <div className="field">
          <span>{t('teacher.rounds')}</span>
          <div className="level-picks">
            {def.levels.map((l) => {
              const on = levels.includes(l.index);
              return (
                <button
                  type="button" key={l.index} className={`pick${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => setLevels((prev) => prev.includes(l.index)
                    ? prev.filter((x) => x !== l.index)
                    : [...prev, l.index].sort((a, b) => a - b))}
                >
                  {t('msg.level', { n: l.index })} · {t(l.nameKey as StringKey)}
                </button>
              );
            })}
          </div>
          <small>{t('teacher.roundsHint')}</small>
        </div>

        <button
          className="gbtn pri"
          disabled={levels.length === 0}
          onClick={() => {
            void api.post(`/classes/${row.id}/session`, { mode, grade, levels })
              .then(() => navigate(`/host/${row.code}`));
          }}
        >{t('teacher.launch')}</button>
      </section>

      <section className="tcard">
        <h2>{t('teacher.leaderboard')}</h2>
        {boardTeacher && <p className="a-empty">{t('class.of', { name: boardTeacher })}</p>}
        {board.length === 0 ? <p className="a-empty">{t('host.noPlayers')}</p> : (
          <table className="ttable">
            <thead>
              <tr>
                <th>#</th><th>{t('teacher.roster')}</th>
                <th>{t('teacher.score')}</th><th>{t('trophy.matches')}</th>
                <th>{t('teacher.badges')}</th><th>{t('teacher.streak')}</th><th />
              </tr>
            </thead>
            <tbody>
              {board.map((r, i) => (
                <tr key={r.student_id}>
                  <td>{num(i + 1)}</td>
                  <td>{r.nickname}</td>
                  <td>{num(r.total_score)}</td>
                  <td>{num(r.total_matches)}</td>
                  <td>{num(r.badges)}</td>
                  <td>{num(r.day_streak)}</td>
                  <td>
                    <button
                      className="tiny"
                      onClick={() => {
                        if (!window.confirm(t('teacher.removeAsk'))) return;
                        void api.del(`/classes/${row.id}/roster/${r.student_id}`).then(reload);
                      }}
                    >{t('teacher.removeStudent')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="a-empty">{t('teacher.students', { n: roster.length })}</p>
      </section>
    </div>
  );
}
